import {useState, useCallback} from 'react'
import {useRouter} from 'next/router'
import type {Tersed} from '@/_new-code/services/kontent-ai/types'
import type {
	PetTraitContentItem,
	PetTraitGroupContentItem,
} from '@/_new-code/products/pet-name-finder/models'
import {shuffleArray} from '@/_new-code/utilities/lists'
import {getPetNames} from '@/_new-code/services/pet-names/client'
import type {TraitsByType} from './pet-trait-card'

interface TraitsHookResult {
	activeTraitGroups: Tersed<PetTraitGroupContentItem>[]
	selectedTraitsByGroup: TraitsByType
	toggleTrait: (
		traitName: Tersed<PetTraitContentItem>,
		groupTitle: string
	) => void
	reset: () => void
	isButtonDisabled: boolean
}

export function useTraits(
	defaultTraitGroups: Tersed<PetTraitGroupContentItem>[]
): TraitsHookResult {
	const traitsByType: TraitsByType = {}
	defaultTraitGroups.forEach((group) => {
		traitsByType[group.elements.displayTitle] = null

		group.elements.traitBlocks[0]?.elements.nestedTraitGroups.forEach(
			(nestedGroup) => {
				traitsByType[nestedGroup.elements.displayTitle] = null
			}
		)
	})

	const [selectedTraitsByGroup, setSelectedTraitsByGroup] =
		useState(traitsByType)

	const toggleTrait = (
		petTrait: Tersed<PetTraitContentItem>,
		groupTitle: string
	): void => {
		if (
			selectedTraitsByGroup[groupTitle]?.elements.displayName ===
			petTrait.elements.displayName
		) {
			setSelectedTraitsByGroup((oldState) => ({
				...oldState,
				[groupTitle]: null,
			}))
		} else {
			setSelectedTraitsByGroup((oldState) => ({
				...oldState,
				[groupTitle]: petTrait,
			}))
		}
	}

	function reset(): void {
		setSelectedTraitsByGroup(traitsByType)
		document.getElementById('PetNameForm')?.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		})
	}

	const species = defaultTraitGroups[0]
	const activeTraitGroups = [...defaultTraitGroups]
	const selectedSpecie = species?.elements.traitBlocks.find(
		(trait) =>
			selectedTraitsByGroup[species.elements.displayTitle]?.elements
				.displayName === trait.elements.displayName
	)
	if (selectedSpecie) {
		activeTraitGroups.push(...selectedSpecie.elements.nestedTraitGroups)
	} else {
		const defaultSpecie = species?.elements.traitBlocks[0]
		if (defaultSpecie) {
			activeTraitGroups.push(...defaultSpecie.elements.nestedTraitGroups)
		}
	}

	const isButtonDisabled = !Object.keys(selectedTraitsByGroup).some(
		(group) => selectedTraitsByGroup[group] !== null
	)

	return {
		activeTraitGroups,
		selectedTraitsByGroup,
		toggleTrait,
		reset,
		isButtonDisabled,
	}
}

interface NameGeneratorHookResults {
	searchNames: () => Promise<void>
	shuffle: () => void
	topNames: string[]
	otherNames: string[]
	resetResults: () => void
	isLoading: boolean
	error: string | null
}

interface PetNameResponse {
	names: string[]
}

type ApiResponse = (string | PetNameResponse)[]

export function useNameGenerator(
	traitsByGroup: TraitsByType,
	onNoResults: () => void,
	onError?: () => void
): NameGeneratorHookResults {
	const {locale: pageLocale = ''} = useRouter()
	const [allNames, setAllNames] = useState<string[]>([])
	const [unseenNames, setUnseenNames] = useState<string[]>([])
	const [topNames, setTopNames] = useState<string[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const scrollToResults = useCallback(() => {
		const formElement = document.getElementById('PetNameForm')
		if (formElement) {
			const headerOffset = 100
			const elementPosition = formElement.getBoundingClientRect().top
			const offsetPosition =
				elementPosition + window.pageYOffset - headerOffset

			window.scrollTo({
				top: offsetPosition,
				behavior: 'smooth',
			})
		}
	}, [])

	async function searchNames(): Promise<void> {
		setIsLoading(true)
		setError(null)

		try {
			if (!pageLocale) {
				throw new Error('Locale is required')
			}

			const traitsArray: string[] = []
			Object.keys(traitsByGroup)
				.filter((group) => Boolean(traitsByGroup[group]))
				.forEach((group) => {
					traitsArray.push(
						`${group}:${traitsByGroup[group]?.elements.displayName}`
					)
				})

			if (traitsArray.length === 0) {
				throw new Error('At least one trait must be selected')
			}

			const response = (await getPetNames({
				required: traitsArray,
				locale: pageLocale,
			})) as ApiResponse

			const names = response.flatMap((item) => {
				if (typeof item === 'string') {
					return [item]
				}
				if ('names' in item && Array.isArray(item.names)) {
					return item.names
				}
				return []
			})

			if (names.length === 0) {
				onNoResults()
				return
			}

			const shuffledNames = shuffleArray(names)
			const limitedNames = shuffledNames.slice(0, 25)

			setUnseenNames(limitedNames.slice(5))
			setTopNames(limitedNames.slice(0, 5))
			setAllNames(limitedNames.sort())

			scrollToResults()
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Failed to search names')
			onError?.()
		} finally {
			setIsLoading(false)
		}
	}

	function shuffle(): void {
		if (unseenNames.length === 0 && topNames.length > 0) {
			setTopNames([])
			setUnseenNames([])
		} else if (unseenNames.length === 0) {
			const newNames = shuffleArray(allNames)
			setTopNames(newNames.slice(0, 5))
			setUnseenNames(newNames.slice(5))
		} else {
			setTopNames(unseenNames.slice(0, 5))
			setUnseenNames((oldArray) => oldArray.slice(5))
		}

		scrollToResults()
	}

	function resetResults(): void {
		setTopNames([])
		setUnseenNames([])
		setAllNames([])
		setError(null)
	}

	return {
		searchNames,
		shuffle,
		topNames,
		otherNames: allNames,
		resetResults,
		isLoading,
		error,
	}
}
